import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Calendar, CalendarCheck, ClipboardList, Users2, FileText, MessageSquare, UserCog, Settings, LogOut, Menu, Moon, Sun, Inbox, KeyRound, Wallet, Scale, BookOpen, Ticket, Fuel, CarFront, MapPin, RadioTower } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import { useRights, roleLabel } from "@/lib/rights";
import api from "@/lib/api";
import ChatImage from "@/components/ChatImage";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dispatch", label: "Dispatch", icon: RadioTower, managers: true, only: "transport", right: "dispatch" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, managers: true },
  { to: "/calendar", label: "Calendrier", icon: Calendar },
  { to: "/missions", label: "Missions", vocab: "Missions", icon: ClipboardList },
  { to: "/clients", label: "Clients", icon: Users2, managers: true },
  { to: "/vehicules", label: "Véhicules", icon: CarFront, managers: true, only: "transport" },
  { to: "/lieux", label: "Lieux", icon: MapPin, managers: true, only: "transport" },
  { to: "/subscriptions", label: "Abonnements", icon: Ticket, managers: true, only: "conciergerie" },
  { to: "/fuel", label: "Compte carburant", icon: Fuel, managers: true, only: "conciergerie" },
  { to: "/demandes", label: "Demandes", icon: Inbox, managers: true },
  { to: "/journal", label: "Journal", icon: BookOpen, managers: true },
  { to: "/invoices", label: "Facturation", icon: FileText, managers: true },
  { to: "/expenses", label: "Dépenses", icon: Wallet, managers: true },
  { to: "/profitability", label: "Rentabilité", icon: Scale, managers: true },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  { to: "/users", label: "Utilisateurs", icon: UserCog, managers: true, right: "users" },
  { to: "/settings", label: "Réglages", icon: Settings, managers: true, right: "settingsBilling" },
];
// Lot T0 : `only` = page réservée à un métier ; `vocab` = libellé pris dans le vocabulaire du métier (vocab.js).
// Lot T5-0 : `right` = page réservée à un droit (lib/rights.js) ; en conciergerie, tout gestionnaire l'a.
// Lot T5a : « Dispatch » en tête (Patron et dispatcheur du transport), qui devient leur page d'arrivée ; leur Tableau
// de bord passe alors à /dashboard, juste en dessous.
// Barre du bas sur téléphone. Gestionnaires : 4 pages + « Plus » (le reste) ; employés : inchangé (+ « Sortir »).
// Secrétaire administrative (transport, lot T5-0) : Dashboard, Courses, Facturation, Clients, dans cet ordre.
const MOBILE = ["/", "/calendar", "/missions", "/messages"];
const MOBILE_MANAGERS = ["/", "/calendar", "/missions", "/demandes"];
const MOBILE_SECRETARY = ["/", "/missions", "/invoices", "/clients"];
// Patron et dispatcheur du transport (lot T5a) : « Dispatch » remplace « Dashboard » (Dashboard dans « Plus »).
const MOBILE_DISPATCH = ["/dispatch", "/calendar", "/missions", "/demandes"];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const voc = useVocab();
  const rights = useRights();
  const loc = useLocation();
  const nav = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [msgUnread, setMsgUnread] = useState(0);
  const [bookingNew, setBookingNew] = useState(0);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const poll = () => {
      api.get("/messages/unread").then((r) => setMsgUnread(Object.values(r.data).reduce((a, b) => a + b, 0))).catch(() => {});
      if (user.role !== "employee") api.get("/bookings").then((r) => setBookingNew(r.data.filter((b) => b.status === "a_traiter").length)).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 12000);
    return () => clearInterval(id);
  }, [loc.pathname]);

  const items = NAV.filter((n) => (!n.managers || user.role !== "employee") && (!n.only || n.only === voc.business) && (!n.right || rights[n.right]))
    .map((n) => (n.vocab ? { ...n, label: voc[n.vocab] } : n))
    .map((n) => (n.to === "/" && rights.dispatchHome ? { ...n, to: "/dashboard" } : n));
  const isManager = user.role !== "employee";
  const brand = settings?.company_name || "Fiableo";
  const mobileBar = rights.secretaryLimited ? MOBILE_SECRETARY : rights.dispatchHome ? MOBILE_DISPATCH : isManager ? MOBILE_MANAGERS : MOBILE;
  // Dans l'ordre de la barre (pour les barres d'avant, c'est aussi l'ordre du menu : rien ne change).
  const mobileItems = mobileBar.map((to) => items.find((i) => i.to === to)).filter(Boolean).slice(0, 4);
  // Chauffeur en transport (lot T4a) : « Planning » en tête de la barre du bas, pour revenir à « Mon planning ».
  // Employé de la conciergerie : barre inchangée.
  if (!isManager && voc.business === "transport") mobileItems.unshift({ to: "/", label: "Planning", icon: CalendarCheck });
  const moreItems = isManager ? items.filter((i) => !mobileBar.includes(i.to)) : [];
  const [moreOpen, setMoreOpen] = useState(false);

  const doLogout = () => { logout(); nav("/login"); };
  const [pwOpen, setPwOpen] = useState(false);
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const changePw = async () => {
    try {
      await api.post("/auth/change-password", { old_password: pwOld, new_password: pwNew });
      toast.success("Mot de passe modifié");
      setPwOpen(false); setPwOld(""); setPwNew("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {settings?.logo_url ? <div className="h-9 w-9 rounded-xl overflow-hidden bg-muted grid place-items-center shrink-0"><ChatImage path={settings.logo_url} className="h-9 w-9 object-contain" /></div> :
              <div className="h-9 w-9 rounded-xl grid place-items-center bg-primary text-primary-foreground font-display font-black">{brand[0]}</div>}
            <div>
              <div className="font-display font-bold text-lg leading-none tracking-tight">{brand}</div>
              <div className="text-[10px] uppercase tracking-overline text-muted-foreground mt-1">{voc.tagline}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto no-scrollbar">
          {items.map((n) => {
            const active = loc.pathname === n.to;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
                className={cn("flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-colors duration-200",
                  active ? "bg-accent/12 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted")}>
                <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                {n.label}
                {n.to === "/messages" && msgUnread > 0 && <span className="ml-auto text-[10px] font-bold bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="nav-msg-unread">{msgUnread}</span>}
                {n.to === "/demandes" && bookingNew > 0 && <span className="ml-auto text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="nav-demandes-badge">{bookingNew}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-accent/20 grid place-items-center text-sm font-semibold uppercase">{user.name?.[0]}</div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{roleLabel(user.role, voc)}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" onClick={() => setPwOpen(true)} data-testid="change-pw-button">
            <KeyRound className="h-4 w-4" /> Changer mon mot de passe
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" onClick={doLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Hors du menu latéral : ouvert aussi depuis « Plus » sur téléphone */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Changer mon mot de passe</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" placeholder="Mot de passe actuel" value={pwOld} onChange={(e) => setPwOld(e.target.value)} data-testid="pw-old" />
            <Input type="password" placeholder="Nouveau mot de passe (min. 6 caractères)" value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="pw-new" />
            <Button className="w-full" onClick={changePw} disabled={!pwOld || pwNew.length < 6} data-testid="pw-submit">Valider</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2 font-display font-bold text-lg">{brand}</div>
          <div className="hidden md:block text-sm text-muted-foreground">{settings?.timezone}</div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">
              {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-[1400px] w-full">{children}</main>
      </div>

      {/* Bottom nav mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 backdrop-blur-xl bg-background/85 border-t border-border grid" style={{ gridTemplateColumns: `repeat(${mobileItems.length + 1}, 1fr)` }}>
        {mobileItems.map((n) => {
          const active = loc.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} data-testid={`mobilenav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={cn("relative flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors", active ? "text-accent" : "text-muted-foreground")}>
              <n.icon className="h-5 w-5" strokeWidth={1.75} />
              {n.label}
              {n.to === "/messages" && msgUnread > 0 && <span className="absolute mt-[-2.2rem] ml-5 h-2 w-2 rounded-full bg-accent" />}
              {n.to === "/demandes" && bookingNew > 0 && <span className="absolute top-1 left-1/2 ml-2 text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 min-w-[18px] text-center leading-[18px]" data-testid="mobilenav-demandes-badge">{bookingNew}</span>}
            </Link>
          );
        })}
        {isManager ? (
          <button onClick={() => setMoreOpen(true)} data-testid="mobilenav-more"
            className={cn("relative flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors", moreItems.some((n) => n.to === loc.pathname) ? "text-accent" : "text-muted-foreground")}>
            <Menu className="h-5 w-5" strokeWidth={1.75} /> Plus
            {msgUnread > 0 && <span className="absolute top-2 left-1/2 ml-2 h-2 w-2 rounded-full bg-accent" data-testid="mobilenav-more-dot" />}
          </button>
        ) : (
          <button onClick={doLogout} className="flex flex-col items-center gap-1 py-2.5 text-[11px] text-muted-foreground" data-testid="mobile-logout">
            <LogOut className="h-5 w-5" strokeWidth={1.75} /> Sortir
          </button>
        )}
      </nav>

      {/* « Plus » (gestionnaires, téléphone) : les pages qui n'ont pas de place dans la barre du bas */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="md:hidden rounded-t-2xl max-h-[85vh] overflow-y-auto pb-8" data-testid="mobile-more-sheet">
          <SheetHeader><SheetTitle className="font-display text-left">Plus</SheetTitle></SheetHeader>
          <nav className="mt-4 grid grid-cols-1 gap-1">
            {moreItems.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMoreOpen(false)} data-testid={`more-${n.to.slice(1)}`}
                className={cn("flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl", loc.pathname === n.to ? "bg-accent/12 text-foreground" : "text-muted-foreground hover:bg-muted")}>
                <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                {n.label}
                {n.to === "/messages" && msgUnread > 0 && <span className="ml-auto text-[10px] font-bold bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{msgUnread}</span>}
              </Link>
            ))}
            <div className="my-2 border-t border-border" />
            <button onClick={() => { setMoreOpen(false); setPwOpen(true); }} className="flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl text-muted-foreground hover:bg-muted" data-testid="more-change-pw">
              <KeyRound className="h-[18px] w-[18px]" strokeWidth={1.75} /> Changer mon mot de passe
            </button>
            <button onClick={doLogout} className="flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl text-muted-foreground hover:bg-muted" data-testid="more-logout">
              <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} /> Déconnexion
            </button>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}
